import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, Trash2, Plus, Upload, Inbox, Settings as SettingsIcon, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, API_BASE } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";

const TABS = [
  { k: "settings", label: "Settings" },
  { k: "clubs", label: "Career" },
  { k: "goalkeepers", label: "Goalkeepers" },
  { k: "developed", label: "Development" },
  { k: "references", label: "References" },
  { k: "education", label: "Education" },
  { k: "media", label: "Media" },
  { k: "messages", label: "Messages" },
];

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url;
};

const publicUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api")) return `${API_BASE.replace(/\/api$/, "")}${url}`;
  return url;
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--rp-muted)] mb-2">
      {label}
    </div>
    {children}
  </label>
);

const TextInput = (props) => (
  <input
    {...props}
    className="w-full bg-[#101316] border border-white/10 focus:border-[color:var(--rp-accent)] px-4 py-2.5 text-sm text-white outline-none transition-colors"
  />
);

const TextArea = (props) => (
  <textarea
    {...props}
    className="w-full min-h-[80px] bg-[#101316] border border-white/10 focus:border-[color:var(--rp-accent)] px-4 py-2.5 text-sm text-white outline-none transition-colors"
  />
);

// ------------------------------------------------------------------
// Settings panel
// ------------------------------------------------------------------
const SettingsPanel = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings");
    setS(data);
  };

  useEffect(() => {
    load();
  }, []);

  if (!s) return <div className="text-white/50 text-sm">Loading…</div>;

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        hero_image_url: s.hero_image_url,
        profile_image_url: s.profile_image_url,
        email: s.email,
        phone: s.phone,
        cv_url: s.cv_url,
      });
      setS(data);
      toast.success("Settings updated");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const uploadTo = async (field, file) => {
    try {
      const url = await uploadFile(file);
      setS((prev) => ({ ...prev, [field]: url }));
      toast.success("File uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Field label="Hero background image URL">
        <div className="flex gap-3">
          <TextInput value={s.hero_image_url || ""} onChange={set("hero_image_url")} />
          <label className="rp-btn rp-btn-outline cursor-pointer">
            <Upload size={14} /> Upload
            <input type="file" hidden accept="image/*" onChange={(e) => e.target.files[0] && uploadTo("hero_image_url", e.target.files[0])} />
          </label>
        </div>
        {s.hero_image_url && (
          <img src={publicUrl(s.hero_image_url)} alt="hero" className="mt-3 h-32 object-cover border border-white/10" />
        )}
      </Field>
      <Field label="Profile image URL">
        <div className="flex gap-3">
          <TextInput value={s.profile_image_url || ""} onChange={set("profile_image_url")} />
          <label className="rp-btn rp-btn-outline cursor-pointer">
            <Upload size={14} /> Upload
            <input type="file" hidden accept="image/*" onChange={(e) => e.target.files[0] && uploadTo("profile_image_url", e.target.files[0])} />
          </label>
        </div>
        {s.profile_image_url && (
          <img src={publicUrl(s.profile_image_url)} alt="profile" className="mt-3 h-32 object-cover border border-white/10" />
        )}
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Email"><TextInput value={s.email || ""} onChange={set("email")} /></Field>
        <Field label="Phone"><TextInput value={s.phone || ""} onChange={set("phone")} /></Field>
      </div>
      <Field label="CV PDF URL">
        <div className="flex gap-3">
          <TextInput value={s.cv_url || ""} onChange={set("cv_url")} placeholder="Upload a PDF or paste a URL" />
          <label className="rp-btn rp-btn-outline cursor-pointer">
            <Upload size={14} /> Upload PDF
            <input type="file" hidden accept="application/pdf" onChange={(e) => e.target.files[0] && uploadTo("cv_url", e.target.files[0])} />
          </label>
        </div>
      </Field>
      <button onClick={save} disabled={saving} className="rp-btn rp-btn-primary">
        <Save size={14} /> {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
};

// ------------------------------------------------------------------
// Generic CRUD Panel
// ------------------------------------------------------------------
const emptyBilingual = () => ({ es: "", en: "" });

const RESOURCES = {
  clubs: {
    label: "Career club",
    listGet: "/clubs",
    adminBase: "/admin/clubs",
    empty: () => ({
      name: "",
      competition: emptyBilingual(),
      period: "",
      achievements: emptyBilingual(),
      logo_url: "",
      order: 0,
    }),
    render: (item, up) => (
      <div className="grid grid-cols-2 gap-4">
        <Field label="Club name"><TextInput value={item.name || ""} onChange={(e) => up({ name: e.target.value })} /></Field>
        <Field label="Period"><TextInput value={item.period || ""} onChange={(e) => up({ period: e.target.value })} /></Field>
        <Field label="Competition (ES)"><TextInput value={item.competition?.es || ""} onChange={(e) => up({ competition: { ...item.competition, es: e.target.value } })} /></Field>
        <Field label="Competition (EN)"><TextInput value={item.competition?.en || ""} onChange={(e) => up({ competition: { ...item.competition, en: e.target.value } })} /></Field>
        <Field label="Achievements (ES)"><TextArea value={item.achievements?.es || ""} onChange={(e) => up({ achievements: { ...item.achievements, es: e.target.value } })} /></Field>
        <Field label="Achievements (EN)"><TextArea value={item.achievements?.en || ""} onChange={(e) => up({ achievements: { ...item.achievements, en: e.target.value } })} /></Field>
        <Field label="Logo URL"><TextInput value={item.logo_url || ""} onChange={(e) => up({ logo_url: e.target.value })} /></Field>
        <Field label="Order"><TextInput type="number" value={item.order ?? 0} onChange={(e) => up({ order: parseInt(e.target.value || "0", 10) })} /></Field>
      </div>
    ),
    titleOf: (i) => `${i.name || "New club"} · ${i.period || ""}`,
  },
  goalkeepers: {
    label: "Goalkeeper",
    listGet: "/goalkeepers",
    adminBase: "/admin/goalkeepers",
    empty: () => ({
      name: "", club_worked: "", current_club: "", national_team: "",
      description: emptyBilingual(), photo_url: "",
      is_international: false, is_first_division: true, is_developed: false, order: 0,
    }),
    render: (item, up) => (
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name"><TextInput value={item.name || ""} onChange={(e) => up({ name: e.target.value })} /></Field>
        <Field label="Photo URL"><TextInput value={item.photo_url || ""} onChange={(e) => up({ photo_url: e.target.value })} /></Field>
        <Field label="Club (worked with Robert)"><TextInput value={item.club_worked || ""} onChange={(e) => up({ club_worked: e.target.value })} /></Field>
        <Field label="Current club"><TextInput value={item.current_club || ""} onChange={(e) => up({ current_club: e.target.value })} /></Field>
        <Field label="National team"><TextInput value={item.national_team || ""} onChange={(e) => up({ national_team: e.target.value })} /></Field>
        <Field label="Order"><TextInput type="number" value={item.order ?? 0} onChange={(e) => up({ order: parseInt(e.target.value || "0", 10) })} /></Field>
        <Field label="Description (ES)"><TextArea value={item.description?.es || ""} onChange={(e) => up({ description: { ...item.description, es: e.target.value } })} /></Field>
        <Field label="Description (EN)"><TextArea value={item.description?.en || ""} onChange={(e) => up({ description: { ...item.description, en: e.target.value } })} /></Field>
        <div className="col-span-2 flex gap-6">
          <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={!!item.is_international} onChange={(e) => up({ is_international: e.target.checked })} /> International</label>
          <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={!!item.is_first_division} onChange={(e) => up({ is_first_division: e.target.checked })} /> First Division</label>
          <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={!!item.is_developed} onChange={(e) => up({ is_developed: e.target.checked })} /> Developed</label>
        </div>
      </div>
    ),
    titleOf: (i) => i.name || "New goalkeeper",
  },
  developed: {
    label: "Developed goalkeeper",
    listGet: "/developed",
    adminBase: "/admin/developed",
    empty: () => ({ name: "", details: emptyBilingual(), photo_url: "", order: 0 }),
    render: (item, up) => (
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name"><TextInput value={item.name || ""} onChange={(e) => up({ name: e.target.value })} /></Field>
        <Field label="Order"><TextInput type="number" value={item.order ?? 0} onChange={(e) => up({ order: parseInt(e.target.value || "0", 10) })} /></Field>
        <Field label="Details (ES)"><TextArea value={item.details?.es || ""} onChange={(e) => up({ details: { ...item.details, es: e.target.value } })} /></Field>
        <Field label="Details (EN)"><TextArea value={item.details?.en || ""} onChange={(e) => up({ details: { ...item.details, en: e.target.value } })} /></Field>
        <Field label="Photo URL"><TextInput value={item.photo_url || ""} onChange={(e) => up({ photo_url: e.target.value })} /></Field>
      </div>
    ),
    titleOf: (i) => i.name || "New player",
  },
  references: {
    label: "Reference",
    listGet: "/references",
    adminBase: "/admin/references",
    empty: () => ({ name: "", role: emptyBilingual(), category: "coach", order: 0 }),
    render: (item, up) => (
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name"><TextInput value={item.name || ""} onChange={(e) => up({ name: e.target.value })} /></Field>
        <Field label="Category">
          <select className="w-full bg-[#101316] border border-white/10 px-4 py-2.5 text-sm text-white" value={item.category || "coach"} onChange={(e) => up({ category: e.target.value })}>
            <option value="coach">Coach</option>
            <option value="management">Management</option>
          </select>
        </Field>
        <Field label="Role (ES)"><TextInput value={item.role?.es || ""} onChange={(e) => up({ role: { ...item.role, es: e.target.value } })} /></Field>
        <Field label="Role (EN)"><TextInput value={item.role?.en || ""} onChange={(e) => up({ role: { ...item.role, en: e.target.value } })} /></Field>
        <Field label="Order"><TextInput type="number" value={item.order ?? 0} onChange={(e) => up({ order: parseInt(e.target.value || "0", 10) })} /></Field>
      </div>
    ),
    titleOf: (i) => i.name || "New reference",
  },
  education: {
    label: "Education entry",
    listGet: "/education",
    adminBase: "/admin/education",
    empty: () => ({ title: emptyBilingual(), institution: "", date: "", highlighted: false, order: 0 }),
    render: (item, up) => (
      <div className="grid grid-cols-2 gap-4">
        <Field label="Title (ES)"><TextInput value={item.title?.es || ""} onChange={(e) => up({ title: { ...item.title, es: e.target.value } })} /></Field>
        <Field label="Title (EN)"><TextInput value={item.title?.en || ""} onChange={(e) => up({ title: { ...item.title, en: e.target.value } })} /></Field>
        <Field label="Institution"><TextInput value={item.institution || ""} onChange={(e) => up({ institution: e.target.value })} /></Field>
        <Field label="Date"><TextInput value={item.date || ""} onChange={(e) => up({ date: e.target.value })} /></Field>
        <Field label="Order"><TextInput type="number" value={item.order ?? 0} onChange={(e) => up({ order: parseInt(e.target.value || "0", 10) })} /></Field>
        <label className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={!!item.highlighted} onChange={(e) => up({ highlighted: e.target.checked })} /> Highlighted (Pro License)</label>
      </div>
    ),
    titleOf: (i) => i.title?.es || i.title?.en || "New entry",
  },
  media: {
    label: "Media item",
    listGet: "/media",
    adminBase: "/admin/media",
    empty: () => ({ title: emptyBilingual(), description: emptyBilingual(), kind: "video", url: "", thumbnail_url: "", order: 0 }),
    render: (item, up) => (
      <div className="grid grid-cols-2 gap-4">
        <Field label="Title (ES)"><TextInput value={item.title?.es || ""} onChange={(e) => up({ title: { ...item.title, es: e.target.value } })} /></Field>
        <Field label="Title (EN)"><TextInput value={item.title?.en || ""} onChange={(e) => up({ title: { ...item.title, en: e.target.value } })} /></Field>
        <Field label="Kind">
          <select className="w-full bg-[#101316] border border-white/10 px-4 py-2.5 text-sm text-white" value={item.kind || "video"} onChange={(e) => up({ kind: e.target.value })}>
            <option value="video">Video (YouTube / Vimeo)</option>
            <option value="image">Image</option>
          </select>
        </Field>
        <Field label="URL"><TextInput value={item.url || ""} onChange={(e) => up({ url: e.target.value })} /></Field>
        <Field label="Thumbnail URL (optional)"><TextInput value={item.thumbnail_url || ""} onChange={(e) => up({ thumbnail_url: e.target.value })} /></Field>
        <Field label="Order"><TextInput type="number" value={item.order ?? 0} onChange={(e) => up({ order: parseInt(e.target.value || "0", 10) })} /></Field>
        <Field label="Description (ES)"><TextArea value={item.description?.es || ""} onChange={(e) => up({ description: { ...item.description, es: e.target.value } })} /></Field>
        <Field label="Description (EN)"><TextArea value={item.description?.en || ""} onChange={(e) => up({ description: { ...item.description, en: e.target.value } })} /></Field>
      </div>
    ),
    titleOf: (i) => i.title?.es || i.title?.en || "New media",
  },
};

const CrudPanel = ({ resource }) => {
  const cfg = RESOURCES[resource];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // { id?: str, ...fields }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(cfg.listGet);
    setItems(data);
  };

  useEffect(() => {
    load();
  }, [resource]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...editing };
      delete payload.id;
      delete payload.created_at;
      if (editing.id) {
        await api.patch(`${cfg.adminBase}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(cfg.adminBase, payload);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${cfg.adminBase}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-12 gap-8">
      <div className="col-span-12 lg:col-span-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{cfg.label} · {items.length}</div>
          <button className="rp-btn rp-btn-outline" onClick={() => setEditing(cfg.empty())}>
            <Plus size={14} /> New
          </button>
        </div>
        <ul className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
          {items.map((it) => (
            <li key={it.id}>
              <button
                onClick={() => setEditing({ ...it })}
                className={`w-full text-left px-4 py-3 border transition-colors flex items-center justify-between gap-2 ${
                  editing?.id === it.id ? "border-[color:var(--rp-accent)] bg-[#181c20]" : "border-white/10 hover:border-white/25"
                }`}
              >
                <span className="text-sm text-white truncate">{cfg.titleOf(it)}</span>
                <ChevronRight size={14} className="text-white/40 flex-shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-span-12 lg:col-span-7">
        {editing ? (
          <div className="rp-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-semibold text-white text-lg">
                {editing.id ? "Edit" : "New"} {cfg.label}
              </h3>
              {editing.id && (
                <button className="text-[color:var(--rp-accent)] hover:text-white text-sm inline-flex items-center gap-1" onClick={() => remove(editing.id)}>
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
            {cfg.render(editing, (patch) => setEditing({ ...editing, ...patch }))}
            <div className="flex gap-3 mt-6">
              <button onClick={save} disabled={busy} className="rp-btn rp-btn-primary">
                <Save size={14} /> {busy ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="rp-btn rp-btn-ghost">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="border border-dashed border-white/10 p-12 text-center text-white/40 text-sm">
            Select an item on the left or create a new one.
          </div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Messages panel
// ------------------------------------------------------------------
const MessagesPanel = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/messages");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openMsg = async (m) => {
    setActive(m);
    if (!m.read) {
      try {
        await api.patch(`/admin/messages/${m.id}`);
        load();
      } catch (e) {
        /* ignore */
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/messages/${id}`);
    setActive(null);
    load();
  };

  return (
    <div className="grid grid-cols-12 gap-8">
      <div className="col-span-12 lg:col-span-5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-4 flex items-center gap-2">
          <Inbox size={12} /> Inbox · {items.length}
        </div>
        <ul className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
          {items.length === 0 && <li className="text-white/40 text-sm">No messages yet.</li>}
          {items.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => openMsg(m)}
                className={`w-full text-left px-4 py-3 border transition-colors ${
                  active?.id === m.id ? "border-[color:var(--rp-accent)] bg-[#181c20]" : "border-white/10 hover:border-white/25"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white truncate">{m.name}</span>
                  {!m.read && <span className="w-1.5 h-1.5 bg-[color:var(--rp-accent)]" />}
                </div>
                <div className="text-[11px] text-white/40 mt-1">{m.organization || m.email}</div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-span-12 lg:col-span-7">
        {active ? (
          <div className="rp-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display font-bold text-white text-lg">{active.name}</div>
                <div className="text-[12px] text-white/50">
                  {active.organization && `${active.organization} · `}{active.position}
                </div>
              </div>
              <button onClick={() => remove(active.id)} className="text-[color:var(--rp-accent)] hover:text-white text-sm inline-flex items-center gap-1">
                <Trash2 size={14} /> Delete
              </button>
            </div>
            <div className="text-[13px] text-white/70 space-y-1 border-y border-white/5 py-3 mb-4">
              <div>Email: <a className="text-white hover:text-[color:var(--rp-accent)]" href={`mailto:${active.email}`}>{active.email}</a></div>
              {active.phone && <div>Phone: {active.phone}</div>}
              <div className="text-white/40 text-[11px] tracking-wider uppercase">{active.created_at}</div>
            </div>
            <p className="text-white/85 whitespace-pre-wrap leading-relaxed text-[14px]">{active.message}</p>
          </div>
        ) : (
          <div className="border border-dashed border-white/10 p-12 text-center text-white/40 text-sm">
            Select a message to view.
          </div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Main Dashboard
// ------------------------------------------------------------------
const AdminDashboard = () => {
  const { user, logout, checking } = useAuth();
  const [tab, setTab] = useState("settings");

  const panel = useMemo(() => {
    if (tab === "settings") return <SettingsPanel />;
    if (tab === "messages") return <MessagesPanel />;
    return <CrudPanel resource={tab} />;
  }, [tab]);

  if (checking) return <div className="min-h-screen bg-[#0b0d0f]" />;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid={TEST_IDS.admin.dashboard} className="min-h-screen bg-[#0b0d0f] text-white">
      <header className="border-b border-white/5 bg-[#0b0d0f]/90 backdrop-blur sticky top-0 z-40">
        <div className="rp-container flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <div className="w-7 h-7 border border-white/20 flex items-center justify-center font-display font-bold text-xs">RP</div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-white/70">Admin</div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-[11px] tracking-[0.24em] uppercase text-white/50 hover:text-white">View site</a>
            <button
              data-testid={TEST_IDS.admin.logout}
              onClick={logout}
              className="rp-btn rp-btn-ghost !py-2"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="rp-container py-10 grid grid-cols-12 gap-8">
        <aside className="col-span-12 lg:col-span-2">
          <div className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
            {TABS.map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`text-left px-3 py-2 text-[12px] tracking-[0.14em] uppercase whitespace-nowrap transition-colors border-l-2 ${
                  tab === t.k ? "border-[color:var(--rp-accent)] text-white bg-white/5" : "border-transparent text-white/50 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </aside>
        <main className="col-span-12 lg:col-span-10">
          {panel}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
